import { useCallback, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { TFunction } from 'i18next';
import { PencilIcon } from 'lucide-react';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import * as yup from 'yup';

import MarkdownEditorBlockNote from '@/components/atoms/MarkdownEditor/MarkdownEditorBlockNote';
import { Button } from '@/components/ui/button';
import { useSendCorrection } from '@/hooks/useSendCorrection';
import { cn } from '@/lib/utils';
import { submitCorrection } from '@/modules/tryIt/redux/actions';
import { AMEND_BUTTON_CLASS } from '@/modules/tryIt/RightSide/Panels/DetailsPanel/AmendWrapper';
import { AmendPayload } from '@/shared/types/tryIt';
import { delay, formatErrorMessage } from '@/util/util';

interface Props {
  children?: React.ReactNode;
  footer?: React.ReactNode;
  isAmended: boolean;
  amendPayload: AmendPayload;
  correctionText?: string;
  originalText?: string;
  flat?: boolean;
  turnRow?: boolean;
}

interface Form {
  correction: string;
}

const MAX_FORM_CHARACTERS = 1500;

const schema = (t: TFunction) =>
  yup
    .object({
      correction: yup
        .string()
        .max(MAX_FORM_CHARACTERS, t('validation.max_length', { 0: MAX_FORM_CHARACTERS }))
        .required(t('validation.required')),
    })
    .required();

export const AmendWrapper = ({
  children = <></>,
  footer,
  amendPayload,
  isAmended,
  correctionText,
  originalText,
  flat = false,
  turnRow = false,
}: Props) => {
  const dispatch = useDispatch();
  const detailsRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { sendCorrection, isPending: isAmending } = useSendCorrection();
  const {
    setValue,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Form>({ resolver: yupResolver(schema(t)) as Resolver<Form> });
  const correction = useWatch({ control, name: 'correction' });

  const [show, setShow] = useState(false);
  const [amended, setAmended] = useState(isAmended);

  const onSubmit = async (data: Form) => {
    if (isAmending) {
      return null;
    }
    const payload = { ...amendPayload, correction: data.correction };

    try {
      const result = await sendCorrection(payload);
      if (result.status == 'ok') {
        dispatch(submitCorrection(payload));
        setShow(false);
        if (turnRow) {
          toast.success(t('try_it.amendment_submitted'));
        }
        await delay(300);
        setAmended(true);
      }
    } catch {
      // useSendCorrection surfaces the error via toast; swallow here to keep UI state consistent.
    }
  };

  const handleShowClick = useCallback(() => {
    if (!show) {
      setValue('correction', correctionText ?? originalText ?? '');
      setShow(true);
      requestAnimationFrame(() => {
        const editable = detailsRef.current?.querySelector<HTMLElement>('[contenteditable="true"]');
        if (editable) {
          editable.focus();
          editable.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }
  }, [show, setValue, correctionText, originalText]);

  const handleEdit = () => {
    setValue('correction', correctionText ?? '');
    setAmended(false);
    handleShowClick();
  };

  if (!amendPayload) {
    return (
      <div>
        {children}
        {footer}
      </div>
    );
  }

  if (amended) {
    return (
      <div>
        {children}
        <div
          className={cn(
            'mt-1 flex flex-wrap items-center gap-1.5',
            turnRow && 'mt-3 h-6 w-full flex-nowrap justify-between self-stretch'
          )}
        >
          {footer}
          {turnRow ? (
            <button
              type="button"
              onClick={handleEdit}
              data-amend-trigger
              className={cn(AMEND_BUTTON_CLASS, 'ml-auto')}
            >
              <PencilIcon size={16} />
              {t('common.edit')}
            </button>
          ) : (
            <>
              <span className="text-sm leading-none font-medium text-success">
                {t('try_it.amended')}
              </span>
              <span className="text-xs leading-4 font-normal text-muted-foreground">·</span>
              <Button
                variant="ghost-neutral"
                size="sm"
                className="h-auto min-w-0 p-0 leading-none text-muted-foreground hover:bg-transparent"
                onClick={handleEdit}
              >
                <PencilIcon size={16} />
                {t('common.edit')}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn(turnRow && 'flex flex-col gap-3 self-stretch')}>
      {children}
      <div className={cn(turnRow && 'self-stretch')}>
        <div
          className={cn(
            'mt-1 flex flex-wrap items-center gap-1',
            turnRow && 'mt-0 h-6 w-full flex-nowrap items-center justify-between gap-2 self-stretch'
          )}
        >
          {footer}
          {turnRow ? (
            <button
              type="button"
              onClick={handleShowClick}
              data-amend-trigger
              className={cn(AMEND_BUTTON_CLASS, 'ml-auto')}
            >
              <PencilIcon size={16} />
              {t('try_it.amend')}
            </button>
          ) : (
            <Button
              variant="ghost"
              size={flat ? 'flush' : 'sm'}
              className={cn(!flat && 'h-auto min-w-0 p-0')}
              onClick={handleShowClick}
            >
              {t('try_it.amend')}
              <PencilIcon size={16} />
            </Button>
          )}
        </div>

        <div
          data-amend-open={show}
          className={cn(
            'grid transition-all duration-200 ease-in-out',
            show ? 'mt-2 grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
            show && turnRow && 'mt-3'
          )}
        >
          <div
            className={cn(
              show ? 'overflow-visible' : 'overflow-hidden',
              turnRow && 'flex flex-col items-start gap-3 self-stretch',
              // The collapse clips content, not padding, so a closed panel keeps none.
              turnRow && show && 'py-2'
            )}
            ref={detailsRef}
          >
            {/* `contents`: the wrapper is new, so it must not alter the legacy layout. */}
            <div
              className={cn(
                turnRow ? 'flex flex-col items-start gap-2 self-stretch p-0' : 'contents'
              )}
            >
              <div className={cn('rounded-md bg-background', turnRow && 'self-stretch')}>
                <MarkdownEditorBlockNote<Form>
                  name="correction"
                  setValue={setValue}
                  className={turnRow ? 'max-h-96 resize-y overflow-auto' : undefined}
                  error={!!errors.correction}
                  errorMessage={turnRow ? undefined : formatErrorMessage(errors.correction)}
                  value={correction}
                />
              </div>

              <div className={cn(turnRow ? 'flex flex-col gap-1.5 self-stretch' : 'contents')}>
                <span
                  className={cn(
                    'font-normal text-muted-foreground',
                    turnRow ? 'self-stretch text-sm leading-5' : 'text-xs'
                  )}
                >
                  {t('try_it.upon_approval')}
                </span>

                {turnRow && errors.correction && (
                  <p className="self-stretch text-sm leading-5 font-normal text-warning">
                    {formatErrorMessage(errors.correction)}
                  </p>
                )}
              </div>
            </div>
            <div className={cn('mt-3 flex justify-end gap-2', turnRow && 'mt-0 self-stretch')}>
              <Button variant="ghost-neutral" size="sm" onClick={() => setShow(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" size="sm" onClick={handleSubmit(onSubmit)}>
                {t('common.submit')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
